import { PayerAdjustmentGroupType } from '../../api';
import { formatPayerAdjustmentGroupShortCode, PAYER_ADJUSTMENT_GROUP_TYPE_VALUES } from '../dictionary';

/**
 * What identifies a denial, and what identifies a rejection — which are not the same thing.
 *
 * ## Why this module exists
 *
 * A **rejection** is refused before adjudication: the payer (or the clearinghouse) will not accept
 * the claim into its system at all, and answers on a 277CA / 999 / TA1 with a Claim Status Category
 * Code and a Claim Status Code — `A7:562`. Nothing was adjudicated, so there is no allowed amount,
 * no payment, and **no CARC or RARC**, because those live on the CAS segment of an 835 that was
 * never produced.
 *
 * A **denial** is the opposite end: the claim was adjudicated and the payer decided against paying
 * it. That answer arrives on an 835 with a group code, a Claim Adjustment Reason Code and often a
 * Remittance Advice Remark Code — `CO:197.N54`.
 *
 * The platform already draws this line: `ClaimTransmissionStatusType` has `REJECTED` and `DENIED`
 * on either side of `ADJUDICATED`.
 *
 * The design prototype does not. It gives every rejection `carc: "CO-16"` and hides the real code
 * in a prose field (`"277CA edit A7:562. Member ID submitted with…"`). That is why this module is a
 * discriminated union rather than one struct with optional fields: an optional CARC on a rejection
 * is a field that can be populated with something that cannot exist.
 *
 * ## Why the group key is here and not at the call site
 *
 * The denials dashboard groups rows by code, and a worker configuration triggers on a code. If
 * those two ever compute the key differently, the dashboard will show a green WORKER badge for a
 * class the engine never actually matches — automation that silently does not run, displayed as
 * success. `adjudicationGroupKey` is the one function both sides must use.
 */

/** Which side of adjudication a code came from. */
export type AdjudicationKind = 'DENIAL' | 'REJECTION';

/** Post-adjudication: an 835 CAS adjustment. */
export interface IDenialCode {
    kind: 'DENIAL';
    /** CARC group — contractual, patient responsibility, and so on. */
    group: PayerAdjustmentGroupType;
    /** Claim Adjustment Reason Code, bare: `197`, `45`, `B7`. */
    reasonCode: string;
    /** Remittance Advice Remark Code, optional: `N54`, `MA130`. A denial need not carry one. */
    remarkCode?: string;
}

/** Pre-adjudication: a 277CA / 999 / TA1 acknowledgement that refused the claim. */
export interface IRejectionCode {
    kind: 'REJECTION';
    /** Claim Status Category Code: `A3`, `A7`, `A8`. */
    statusCategoryCode: string;
    /** Claim Status Code, bare: `562`, `509`. */
    statusCode: string;
}

export type AdjudicationCode = IDenialCode | IRejectionCode;

/**
 * CARC group enum → the prefix a remit prints.
 *
 * DERIVED, not declared. This mapping already lived in `dictionary/payer-adjustment-groups.ts`
 * (`SHORT_CODE_BY_GROUP`, there since 2026-07-08). An earlier revision of this file re-typed it and
 * called the copy "the single source of truth", which made three copies rather than one — the
 * dictionary's, a `switch` in `claim-payer-summary.service.ts`, and this (GP-16). It is now computed
 * from the dictionary's exported formatter, so the dictionary is the only place a group's prefix is
 * written: `claim-payer-summary` reads this, and this reads the dictionary.
 */
export const ADJUSTMENT_GROUP_PREFIX = Object.fromEntries(
    PAYER_ADJUSTMENT_GROUP_TYPE_VALUES.map(group => [group, formatPayerAdjustmentGroupShortCode(group)]),
) as Record<PayerAdjustmentGroupType, string>;

/**
 * Groups whose balance the PATIENT owes.
 *
 * Only `PR` moves a balance to the patient. `CO` is a contractual write-off the provider absorbs —
 * billing a patient for one is a compliance problem, not a UX choice — and the design prototype
 * offers "Patient collections" on every claim regardless of group. Anything that routes a balance
 * to a statement must check this first.
 */
const PATIENT_RESPONSIBLE_GROUPS: PayerAdjustmentGroupType[] = [PayerAdjustmentGroupType.PATIENTRESPONSIBILITY];

/** `A7`, `A3` — one letter then one digit. Deliberately narrow; a category is not free text. */
const STATUS_CATEGORY = /^[A-Z][0-9]$/;
/** `562`, `24` — the numeric Claim Status Code. */
const STATUS_CODE = /^[0-9]{1,4}$/;
/** `85`, `QC`, `1P` — an Entity Identifier Code, the third part of a 277CA status composite. */
const ENTITY_IDENTIFIER_CODE = /^[A-Z0-9]{2,3}$/;
/** `RX` — a Code List Qualifier Code, the composite's optional fourth part. */
const CODE_LIST_QUALIFIER_CODE = /^[A-Z0-9]{1,3}$/;
/** `197`, `45`, `B7`, `W1` — CARCs are mostly numeric but a few carry a letter. */
const REASON_CODE = /^[A-Z]?[0-9]{1,3}$/;
/** `N54`, `M76`, `MA130` — one or two letters then digits. */
const REMARK_CODE = /^[A-Z]{1,2}[0-9]{1,3}$/;

const normalise = (value: string | undefined): string => (value || '').trim().toUpperCase();

/**
 * Build a denial identity, or `undefined` when the parts are not a denial code.
 *
 * Returns `undefined` rather than throwing or coercing: an unparseable code should drop out of a
 * grouping, not become a group of its own called `undefined-undefined`.
 */
export const parseDenialCode = (
    group: PayerAdjustmentGroupType | undefined,
    reasonCode: string | undefined,
    remarkCode?: string,
): IDenialCode | undefined => {
    const reason = normalise(reasonCode);
    const remark = normalise(remarkCode);

    if (!group || !ADJUSTMENT_GROUP_PREFIX[group] || !REASON_CODE.test(reason)) {
        return undefined;
    }

    return {
        kind: 'DENIAL',
        group,
        reasonCode: reason,
        // A malformed remark is dropped rather than carried — the denial is still identifiable
        // without one, and a bad remark would split one group into two.
        ...(REMARK_CODE.test(remark) ? { remarkCode: remark } : {}),
    };
};

/**
 * Build a rejection identity from a category and a status code.
 *
 * Accepts them separately, or joined the way a 277CA quotes them: its STC01 composite, `A7:562`,
 * which may go on to name the entity whose data was wrong and a code list qualifier — `A7:562:85`,
 * `A7:562:85:RX`. The entity is not part of the identity: whether `A7:562:85` and `A7:562:82` are
 * one group or two is for the rejection report to settle (FE-73 · B1). The entity and qualifier are
 * checked for shape only, not against the code lists.
 */
export const parseRejectionCode = (
    statusCategoryCode: string | undefined,
    statusCode?: string,
): IRejectionCode | undefined => {
    let category = normalise(statusCategoryCode);
    let status = normalise(statusCode);

    // `parseRejectionCode('A7:562')` — the single-argument form. Anything beyond the four parts of a
    // composite, or a part of the wrong shape, means the value is not one, and it is refused rather
    // than read as its first two parts: `A7:562:extra` must not group under A7:562.
    if (!status && category.includes(':')) {
        const [left, right, entity, qualifier, ...rest] = category.split(':');
        if (
            rest.length > 0 ||
            (entity !== undefined && !ENTITY_IDENTIFIER_CODE.test(normalise(entity))) ||
            (qualifier !== undefined && !CODE_LIST_QUALIFIER_CODE.test(normalise(qualifier)))
        ) {
            return undefined;
        }
        category = normalise(left);
        status = normalise(right);
    }

    if (!STATUS_CATEGORY.test(category) || !STATUS_CODE.test(status)) {
        return undefined;
    }

    // Leading zeros are not significant in a status code, and `A7:062` and `A7:62` are the same
    // rejection. Normalising here stops them becoming two groups.
    return { kind: 'REJECTION', statusCategoryCode: category, statusCode: String(Number(status)) };
};

/**
 * The short form shown on a chip: `CO-197` or `A7:562`.
 *
 * The hyphen for denials is not a style choice — it is how a remit prints a group and reason, and
 * it is already what `claim-payer-summary` renders.
 */
export const formatCodeChip = (code: AdjudicationCode): string =>
    code.kind === 'DENIAL'
        ? `${ADJUSTMENT_GROUP_PREFIX[code.group]}-${code.reasonCode}`
        : `${code.statusCategoryCode}:${code.statusCode}`;

/**
 * The full form shown in the dashboard's `Codes` column: `CO:197.N54`, `CO:29` or `A7:562`.
 *
 * A denial with no remark code prints without the trailing `.` — an empty remark is absence, not a
 * blank value.
 */
export const formatCodeComposite = (code: AdjudicationCode): string => {
    if (code.kind === 'REJECTION') {
        return `${code.statusCategoryCode}:${code.statusCode}`;
    }
    const head = `${ADJUSTMENT_GROUP_PREFIX[code.group]}:${code.reasonCode}`;
    return code.remarkCode ? `${head}.${code.remarkCode}` : head;
};

/**
 * The canonical grouping key — the one the dashboard groups by AND the one a worker triggers on.
 *
 * Both sides must call this. If the dashboard groups by one key and the worker engine matches on
 * another, the WORKER badge claims coverage that never runs: a silent failure that reads as
 * success, which is the worst shape a bug can take on this page.
 *
 * `kind` is part of the key **defensively**, not because the two can collide today: denial prefixes
 * are letters (`CO`, `PR`, `OA`, `PIR`, `CR`) and rejection categories are a letter then a digit
 * (`A7`), so `CO:16` and `A7:16` are already distinct. It is here so the key stays unambiguous if
 * either format ever widens, and so a key is self-describing when it turns up in a log or a
 * worker's stored trigger. Do not remove it on the grounds that the prefixes happen not to overlap
 * — that is a property of the current code lists, not of the key.
 */
export const adjudicationGroupKey = (code: AdjudicationCode): string =>
    `${code.kind}:${formatCodeComposite(code)}`;

/** Whether this code makes the balance the PATIENT's. See `PATIENT_RESPONSIBLE_GROUPS`. */
export const isPatientResponsible = (code: AdjudicationCode): boolean =>
    code.kind === 'DENIAL' && PATIENT_RESPONSIBLE_GROUPS.includes(code.group);

/**
 * Whether an allowed amount can exist for this code.
 *
 * False for every rejection: the claim never adjudicated, so there is no allowed amount to be under
 * — only a charge at risk. Any money column keyed on "allowed" must check this before rendering,
 * or it will show a denial's money shape on a row where that money does not exist.
 */
export const hasAllowedAmount = (code: AdjudicationCode): boolean => code.kind === 'DENIAL';
